import type { MaybeRef } from '@vueuse/core';

import { useWindowScroll } from '@vueuse/core';
import { usePageSectionsStore } from '~/store/pageSectionStore';
import { unref } from 'vue';

/**
 * Configuration options for controlling the contextual header visibility
 */
export interface UseContextualHeaderVisibilityControlOptions {
	/**
	 * Reference to the hero section element that appears at the top of the page.
	 * The contextual header will not be visible when the user is viewing this section.
	 */
	heroSectionRef?: MaybeRef<HTMLElement | null>;

	/**
	 * Reference to the section that determines when the contextual header should be hidden.
	 * When the user scrolls past this section, the header will be hidden again.
	 * If not provided, the first content section will be used as a fallback.
	 */
	anchorSection?: MaybeRef<HTMLElement | null>;

	/**
	 * Array of references to all section elements that should be tracked for visibility.
	 * These sections will be registered with the store and used to determine which
	 * section is currently active in the contextual header navigation.
	 * Each section should have an 'id' attribute and a 'data-section-name' attribute
	 * to properly display in the navigation.
	 * Order of elements in the array will determine the order of the sections in the navigation.
	 */
	sectionRefs: MaybeRef<HTMLElement | null>[];
}

/**
 * Composable to control the visibility of the contextual header based on scroll position.
 * The header will be shown or hidden based on the user's scroll position relative to
 * the configured sections.
 *
 * @param {UseContextualHeaderVisibilityControlOptions} options - Configuration options for the header visibility
 */
export function useContextualHeaderVisibilityControl(
	options: UseContextualHeaderVisibilityControlOptions
) {
	const pageSectionsStore = usePageSectionsStore();
	const isContextualHeaderVisible = inject(
		'isContextualHeaderVisible',
		ref(false)
	);
	const { y: scrollY } = useWindowScroll();

	const getFirstSection = (): HTMLElement | null => {
		const sections = Array.from(pageSectionsStore.sectionRefs.values());
		return (
			sections.find(
				el =>
					el.id.includes('overview') ||
					el.getAttribute('data-section-priority') === '1'
			) || null
		);
	};

	const updateContextualHeader = (tabId: string) => {
		if (pageSectionsStore.isAutomaticScrollingInteracting) return;
		isContextualHeaderVisible.value = true;
		pageSectionsStore.setActiveTabId(tabId);
	};

	const getElementTopPosition = (
		element: HTMLElement | null | undefined
	): number => {
		if (!element) return 0;
		return element.getBoundingClientRect().top + window.scrollY;
	};

	const isElementVisible = (
		element: HTMLElement | null | undefined
	): boolean => {
		if (!element) return false;

		const rect = element.getBoundingClientRect();
		const height = window.innerHeight;

		return rect.top < height * 0.7 && rect.bottom > 0;
	};

	const getMostVisibleElement = (
		elements: HTMLElement[]
	): HTMLElement | null => {
		if (elements.length === 0) return null;

		const visibleElements = elements.filter(el => isElementVisible(el));

		if (visibleElements.length === 0) return null;

		return visibleElements.reduce((prev, current) => {
			const prevRect = prev.getBoundingClientRect();
			const currentRect = current.getBoundingClientRect();

			const prevVisibleHeight = Math.min(window.innerHeight, prevRect.bottom);
			const currentVisibleHeight = Math.min(
				window.innerHeight,
				currentRect.bottom
			);

			return prevVisibleHeight > currentVisibleHeight ? prev : current;
		});
	};

	const shouldHideHeader = (
		scrollPositionY: number,
		chosenSectionTop: number,
		offsetTolerance: number
	): boolean => {
		const chosenSectionThreshold = chosenSectionTop - offsetTolerance;
		const heroSectionElement = options.heroSectionRef
			? unref(options.heroSectionRef)
			: null;

		const isWithinHeroSection = heroSectionElement
			? heroSectionElement.getBoundingClientRect().bottom - offsetTolerance > 0
			: false;

		return isWithinHeroSection || scrollPositionY > chosenSectionThreshold;
	};

	const updateHeaderBasedOnScroll = () => {
		const sections = Array.from(pageSectionsStore.sectionRefs.values());
		const mostVisibleElement = getMostVisibleElement(sections);

		if (mostVisibleElement) {
			updateContextualHeader(mostVisibleElement.id);
		} else {
			const firstSection = sections[0];
			const secondSection = sections[1];

			if (firstSection) {
				if (!secondSection) {
					updateContextualHeader(firstSection.id);
					return;
				}

				const secondSectionTop = getElementTopPosition(secondSection);
				const currentScrollY = scrollY.value;

				if (currentScrollY < secondSectionTop) {
					updateContextualHeader(firstSection.id);
				} else if (secondSection.id) {
					updateContextualHeader(secondSection.id);
				}
			}
		}
	};

	const handleScroll = async (scrollYPosition: number) => {
		updateHeaderBasedOnScroll();

		const anchorSection = options.anchorSection
			? unref(options.anchorSection)
			: getFirstSection();

		const anchorSectionTop = anchorSection?.offsetTop ?? 0;
		const scrollTolerance = 200;

		isContextualHeaderVisible.value = !shouldHideHeader(
			scrollYPosition,
			anchorSectionTop,
			scrollTolerance
		);

		if (!isContextualHeaderVisible.value) {
			const heroSectionElement = options.heroSectionRef
				? unref(options.heroSectionRef)
				: null;
			if (heroSectionElement?.id) {
				pageSectionsStore.setActiveTabId(heroSectionElement.id);
			}
		}
	};

	watchThrottled(
		scrollY,
		() => {
			handleScroll(scrollY.value);
		},
		{
			throttle: 200
		}
	);

	watchEffect(() => {
		if (!import.meta.client) return;

		const registerSection = (ref: MaybeRef<HTMLElement | null> | undefined) => {
			if (!ref) return;
			const element = unref(ref);
			if (element && element.id) {
				pageSectionsStore.setSectionRef(element);
			}
		};

		if (options.heroSectionRef) {
			registerSection(options.heroSectionRef);
		}

		options.sectionRefs.forEach(ref => {
			registerSection(ref);
		});
	});

	return {
		isContextualHeaderVisible
	};
}
